import TailInIcon from "../../../../components/icons/TailInIcon";
import TailOutIcon from "../../../../components/icons/TailOutIcon";
import Avatar from "../../../../components/generic/Avatar";
import dayjs from "../../../../utils/dayjs";
import { formatFileSize, getFullName } from "../../../../utils/shared";
import { DownloadOutlined } from "@ant-design/icons";
import { Button, message as adMessage } from "antd";
import { handleDownload } from "../../../../utils/files";
import { useCallback, useEffect, useMemo } from "react";
import { FILE_BASE_URL } from "../../../../config";
import PrintableImage from "../../../../components/shared/PrintableImage";
import { renderContentWithLinks } from "./LinkUtils";
import { JOIN_MEETING_TOKEN } from "../../../../constant";
import JoinMeeting from "./JoinMetting";
import MessageReactions from "./MessageReactions";
import ReactionsList from "./ReactionsList";
import { useDispatch } from "react-redux";
import { updateFileKey } from "../../../../store/chats/thunks";
import Audio from "../../../../components/shared/Audio";
import Video from "../../../../components/shared/Video";
import ContactMessage from "./ContactMessage";

const MessageItem = ({ message, isCurrentUser, users }) => {
  const { message_attachment: attachment, content } = message;

  const dispatch = useDispatch();

  const { fileUrl, isImage, isAudio, isVideo } = useMemo(() => {
    const isImage = attachment?.mime_type?.startsWith("image");
    const isAudio = attachment?.mime_type?.startsWith("audio");
    const isVideo = attachment?.mime_type?.startsWith("video");

    let fileUrl = null;

    if (attachment?.file) {
      fileUrl = URL.createObjectURL(attachment.file);
    } else if (attachment?.storage_url) {
      fileUrl = `${FILE_BASE_URL}${attachment.storage_url}`;
    }

    return { fileUrl, isImage, isAudio, isVideo };
  }, [attachment]);

  useEffect(() => {
    return () => {
      if (attachment?.file && fileUrl) {
        URL.revokeObjectURL(fileUrl);
      }
    };
  }, [attachment?.file, fileUrl]);
  const isSystem = message?.sender_type === "system";

  const onError = useCallback(async () => {
    return await dispatch(
      updateFileKey({
        attachment_id: attachment?.id,
        message_id: message?.id,
        conversation_id: message?.conversation_id,
      }),
    );
  }, [message, attachment]);

  const downloadWithFallback = useCallback(async () => {
    try {
      const failed = await handleDownload(fileUrl, attachment?.filename);

      if (!failed) return;

      const { payload } = await onError();

      if (!payload?.url) throw new Error("URL Not Found");

      const retryFailed = await handleDownload(
        `${FILE_BASE_URL}${payload.url}`,
        attachment?.filename,
      );

      if (retryFailed) throw new Error("URL Not Found");
    } catch (error) {
      adMessage.error("Download failed. Please try again after some time.");
    }
  }, [fileUrl, attachment]);

  return (
    <div
      className={`message-wrapper ${isCurrentUser ? "own-message" : ""} ${isSystem ? "system-message" : ""}`}
    >
      {!isCurrentUser && (
        <Avatar size={32} className="message-avatar" user={message?.sender} />
      )}
      <div className="message-body flex">
        {!isCurrentUser && !isSystem && <TailInIcon />}
        <div className="message-bubble">
          {!isCurrentUser && (
            <div
              className={`message-sender ${attachment?.filename ? "sender-with-file" : ""}`}
            >
              {getFullName(message.sender)}
            </div>
          )}

          {attachment?.filename && (
            <div className="attachment-container">
              {isImage ? (
                <div className="image-preview">
                  <PrintableImage
                    src={fileUrl}
                    alt={attachment.filename}
                    key={attachment?.id}
                    onError={onError}
                  />
                  <div
                    className="download-overlay"
                    onClick={() => downloadWithFallback()}
                  >
                    <span className="download-icon">
                      <DownloadOutlined />
                    </span>
                  </div>
                </div>
              ) : isAudio ? (
                <Audio
                  key={attachment?.id}
                  fileUrl={fileUrl}
                  attachment={attachment}
                  onError={onError}
                />
              ) : isVideo ? (
                <Video
                  key={attachment?.id}
                  fileUrl={fileUrl}
                  attachment={attachment}
                  onError={onError}
                />
              ) : (
                <div className="file-attachment" onClick={downloadWithFallback}>
                  <div className="file-icon">📄</div>
                  <div className="file-info">
                    <div className="file-name">{attachment.filename}</div>
                    <div className="file-size">
                      {formatFileSize(attachment.file_size_bytes)}
                    </div>
                  </div>
                  <Button
                    className="download-button"
                    icon={<DownloadOutlined />}
                    shape="circle"
                  />
                </div>
              )}
            </div>
          )}

          {message.message_type === "contact" ? (
            <ContactMessage contacts={message.metadata} />
          ) : (
            <>
              {content && (
                <>
                  <div
                    className={`message-content ${isImage || isAudio ? "message-image-content" : ""}`}
                  >
                    {content?.endsWith(JOIN_MEETING_TOKEN) ? (
                      <JoinMeeting message={content} />
                    ) : (
                      renderContentWithLinks({ text: content, users })
                    )}
                  </div>
                  <MessageReactions
                    message={message}
                    reactions={message?.reactions}
                  />
                </>
              )}
            </>
          )}

          <div className="message-time">
            {message?.clientMessageId && isCurrentUser
              ? "sending..."
              : dayjs(message.sent_at).format("DD MMM, hh:mm A")}
          </div>

          {!!message?.reactions?.length && (
            <ReactionsList reactions={message?.reactions} users={users} />
          )}
        </div>
        {isCurrentUser && <TailOutIcon />}
      </div>
    </div>
  );
};

export default MessageItem;
